import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  UsergroupAddOutlined,
  MenuOutlined,
  CloseOutlined,
  LayoutOutlined,
  UserOutlined,
  BellOutlined,
  ArrowRightOutlined,
  DashboardOutlined
} from '@ant-design/icons';
import { Badge, Button, Avatar, Tooltip } from 'antd';
import { baseURL } from '../../config';

// Import your existing images
import logoCollapsed from '../../img/sidebar/logoCollapsed.png';
import logoExpanded from '../../img/sidebar/logoExpanded.png';
import claims from '../../img/sidebar/claim.png';
import dashboard from '../../img/sidebar/dashboard.png';
import employers from '../../img/sidebar/employers.png';
import demoUser from '../../img/sidebar/avatar.png';
import support from '../../img/sidebar/support.png';
import policies from '../../img/sidebar/policies.png';
import fingerprint from '../../img/sidebar/fingerprint.png';
import wallet from '../../img/sidebar/wallet.svg';
import merchant from '../../img/sidebar/merchant.svg';
import provider from '../../img/sidebar/provider.png';
import reportsIcon from '../../img/sidebar/reports.png';
import insightsIcon from '../../img/sidebar/insights.png';
import notificationsIcon from '../../img/sidebar/bell.png';

// Enhanced color palette
const colors = {
  primary: '#00796b',
  secondary: '#004d40',
  success: '#52c41a',
  warning: '#faad14',
  error: '#ff4d4f',
  info: '#1677ff',
  gradients: {
    primary: 'linear-gradient(135deg, #00796b 0%, #004d40 100%)',
    success: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
    error: 'linear-gradient(135deg, #ff4d4f 0%, #cf1322 100%)',
    soft: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
    glass: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
  },
  neutral: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#E8E8E8',
    300: '#D9D9D9',
    400: '#BFBFBF',
    500: '#8C8C8C',
    600: '#595959',
    700: '#434343',
    800: '#262626',
    900: '#1F1F1F'
  }
};

// CSS styles
const sidebarStyles = `
  @keyframes slideIn {
    from {
      transform: translateX(-20px);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes glow {
    0%, 100% {
      box-shadow: 0 0 5px rgba(0, 121, 107, 0.3);
    }
    50% {
      box-shadow: 0 0 20px rgba(0, 121, 107, 0.6), 0 0 30px rgba(0, 121, 107, 0.4);
    }
  }

  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.05);
    }
  }

  .sidebar-container {
    position: fixed;
    left: 0;
    top: 0;
    height: 100vh;
    z-index: 1000;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    border-right: 1px solid rgba(0, 121, 107, 0.1);
    box-shadow: 4px 0 24px rgba(0, 0, 0, 0.08);
    backdrop-filter: blur(10px);
  }

  .sidebar-collapsed {
    width: 80px;
  }

  .sidebar-expanded {
    width: 280px;
  }

  .nav-item {
    position: relative;
    display: flex;
    align-items: center;
    padding: 14px 20px;
    margin: 6px 12px;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    overflow: hidden;
  }

  .nav-item::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
    transition: left 0.5s;
  }

  .nav-item:hover::before {
    left: 100%;
  }

  .nav-item:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 121, 107, 0.15);
  }

  .nav-item.active {
    background: linear-gradient(135deg, #00796b 0%, #004d40 100%);
    color: white;
    box-shadow: 0 8px 24px rgba(0, 121, 107, 0.3);
    animation: glow 2s ease-in-out infinite;
  }

  .nav-item.active .nav-icon {
    filter: brightness(0) invert(1);
  }

  .nav-item:not(.active) .nav-icon {
    filter: brightness(0.6);
    transition: filter 0.3s ease;
  }

  .nav-item:not(.active):hover .nav-icon {
    filter: brightness(0.8);
  }

  .nav-text {
    margin-left: 16px;
    font-weight: 500;
    font-size: 15px;
    animation: slideIn 0.3s ease-out;
  }

  .toggle-button {
    position: absolute;
    top: 20px;
    right: -12px;
    width: 24px;
    height: 24px;
    background: linear-gradient(135deg, #00796b 0%, #004d40 100%);
    border: none;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: white;
    font-size: 12px;
    box-shadow: 0 4px 12px rgba(0, 121, 107, 0.3);
    transition: all 0.3s ease;
    z-index: 1001;
  }

  .toggle-button:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 16px rgba(0, 121, 107, 0.4);
  }

  .user-section {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(135deg, rgba(0, 121, 107, 0.05) 0%, rgba(0, 77, 64, 0.05) 100%);
    backdrop-filter: blur(10px);
    border-top: 1px solid rgba(0, 121, 107, 0.1);
  }

  .user-card {
    padding: 20px;
    transition: all 0.3s ease;
  }

  .user-card:hover {
    background: rgba(0, 121, 107, 0.05);
  }

  .action-button {
    position: absolute;
    right: 20px;
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.6) 100%);
    border: 1px solid rgba(0, 121, 107, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    opacity: 0;
    transform: translateY(10px);
  }

  .user-section:hover .action-button {
    opacity: 1;
    transform: translateY(0);
  }

  .action-button:hover {
    background: linear-gradient(135deg, #00796b 0%, #004d40 100%);
    color: white;
    transform: scale(1.05);
  }

  .logo-container {
    padding: 24px 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-bottom: 1px solid rgba(0, 121, 107, 0.1);
    margin-bottom: 20px;
    position: relative;
  }

  .notification-badge {
    position: absolute;
    top: -2px;
    right: -2px;
    width: 8px;
    height: 8px;
    background: #ff4d4f;
    border-radius: 50%;
    animation: pulse 2s infinite;
  }

  .nav-section-title {
    padding: 12px 20px 8px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: #8C8C8C;
    opacity: 0;
    animation: fadeIn 0.5s ease-out 0.3s forwards;
  }
`;

const EnhancedSideBar = () => {
  const [open, setOpen] = useState(false);
  const [userHover, setUserHover] = useState(false);
  const [current, setCurrent] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  // Inject styles
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = sidebarStyles;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  useEffect(() => {
    setCurrent(location.pathname.split('/')[1]);
  }, [location]);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isRelationshipManager = user?.team_role?.[0]?.role === 'Relationship_Manager';
  const isSuperAdmin = user?.team_role?.some(tr => tr.role === 'Super_Admin');

  const SPOnboarding = isRelationshipManager ? [
    {
      key: 'assigned-providers',
      title: 'Provider Onboarding',
      link: '/assigned-providers',
      icon: provider,
      visible: true,
      width: '24px',
      hasNotification: true
    },
  ] : [
    {
      key: 'providers',
      title: 'Provider Onboarding', 
      link: '/providers',
      icon: provider,
      visible: true,
      width: '24px',
      hasNotification: false
    },
  ];

  const renderNavItem = (item) => {
    const isActive = current === item.key;
    
    return (
      <Tooltip 
        key={item.key}
        title={!open ? item.title : ''}
        placement="right"
        mouseEnterDelay={0.5}
      >
        <div
          className={`nav-item ${isActive ? 'active' : ''}`}
          onClick={() => {
            setCurrent(item.key);
            navigate(item.link);
          }}
        >
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <img 
              src={item.icon}
              className="nav-icon"
              style={{ 
                width: item.width,
                height: item.width,
                objectFit: 'contain'
              }}
              alt={item.title}
            />
            {item.hasNotification && (
              <div className="notification-badge" />
            )}
          </div>
          {open && (
            <span className="nav-text" style={{ color: isActive ? 'white' : colors.neutral[700] }}>
              {item.title}
            </span>
          )}
          {open && (
            <ArrowRightOutlined 
              style={{ 
                marginLeft: 'auto',
                fontSize: '12px',
                opacity: 0.7,
                color: isActive ? 'white' : colors.neutral[500]
              }} 
            />
          )}
        </div>
      </Tooltip>
    );
  };

  return (
    <div className={`sidebar-container ${open ? 'sidebar-expanded' : 'sidebar-collapsed'}`}>
      {/* Toggle Button */}
      <button 
        className="toggle-button"
        onClick={() => setOpen(!open)}
      >
        {open ? <CloseOutlined /> : <MenuOutlined />}
      </button>

      {/* Logo Section */}
      <div className="logo-container">
        <img 
          src={open ? logoExpanded : logoCollapsed} 
          style={{ 
            width: open ? '120px' : '40px',
            height: 'auto',
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            filter: 'drop-shadow(0 4px 8px rgba(0, 121, 107, 0.2))'
          }} 
          alt="Logo"
        />
      </div>

      {/* Navigation Section */}
      <div style={{ 
        flex: 1, 
        paddingTop: '12px',
        paddingBottom: '120px',
        overflowY: 'auto',
        overflowX: 'hidden'
      }}>
        {open && (
          <div className="nav-section-title">
            Navigation
          </div>
        )}
        {SPOnboarding.filter(item => item.visible).map(renderNavItem)}
      </div>

      {/* User Section */}
      <div 
        className="user-section"
        onMouseEnter={() => setUserHover(true)}
        onMouseLeave={() => setUserHover(false)}
      >
        <div className="user-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: open ? '12px' : '0', justifyContent: open ? 'flex-start' : 'center' }}>
            <Avatar
              size={open ? 48 : 40}
              src={demoUser}
              style={{
                border: '2px solid rgba(0, 121, 107, 0.2)',
                boxShadow: '0 4px 12px rgba(0, 121, 107, 0.15)',
                transition: 'all 0.3s ease'
              }}
            />
            {open && (
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ 
                  fontSize: '16px',
                  fontWeight: '600',
                  color: colors.neutral[800],
                  marginBottom: '2px',
                  animation: 'slideIn 0.3s ease-out'
                }}>
                  {user?.name || 'User'}
                </div>
                <div style={{ 
                  fontSize: '13px',
                  color: colors.neutral[500],
                  animation: 'slideIn 0.3s ease-out 0.1s both'
                }}>
                  {user?.team_role?.[0]?.role?.replace('_', ' ') || 'Admin'}
                </div>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          {userHover && open && (
            <div style={{ 
              display: 'flex', 
              gap: '8px', 
              marginTop: '16px',
              animation: 'slideIn 0.3s ease-out'
            }}>
              {isSuperAdmin && (
                <Tooltip title="Manage Users">
                  <Button
                    type="text"
                    size="small"
                    icon={<UsergroupAddOutlined />}
                    onClick={() => navigate('/users')}
                    style={{
                      background: colors.gradients.soft,
                      border: `1px solid ${colors.primary}20`,
                      borderRadius: '8px',
                      color: colors.primary
                    }}
                  />
                </Tooltip>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnhancedSideBar;